import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useFilters } from '@/hooks/useFilters'
import { listContactsQuery } from '@/queries/contact.query'
import { filterToState, stateToFilter } from '@/utils/tableFilterMapper'
import { sortByToState, stateToSortBy } from '@/utils/tableSortMapper'
import { useQuery } from '@tanstack/react-query'
import {
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useDebounce } from '@uidotdev/usehooks'
import * as React from 'react'
import { columns } from './columns'
import { DataTablePagination } from './data-table-pagination'
import { DataTableToolbar } from './data-table-toolbar'

const cleanFilters = {
  name: undefined,
  email: undefined,
  phone: undefined,
  tags: undefined,
}

export function ContactsDataTable() {
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})

  const { filters, setFilters } = useFilters('/_authenticated/contacts/');
  const paginationState = {
    pageIndex: filters.pageIndex ?? 0,
    pageSize: filters.pageSize ?? 10,
  }
  const sortingState = sortByToState(filters.sortBy);
  const filterState = filterToState(filters);
  
  const debouncedFilters = useDebounce(filters, 500);
  const { data: queryResult } = useQuery(listContactsQuery(debouncedFilters));
  
  const table = useReactTable({
    data: queryResult?.data || [],
    columns,
    pageCount: queryResult?.rowCount,
    state: {
      sorting: sortingState,
      columnVisibility,
      rowSelection,
      columnFilters: filterState,
      pagination: paginationState,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updaterOrValue) => {
      const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(sortingState)
              : updaterOrValue;
      setFilters({ sortBy: stateToSortBy(newSortingState) });
    },
    onColumnFiltersChange: (updaterOrValue) => {
      const newFilterState =
        typeof updaterOrValue === "function"
          ? updaterOrValue(filterState)
          : updaterOrValue;
      setFilters(stateToFilter(newFilterState) ?? cleanFilters);
    },
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (pagination) => {
      setFilters(
        typeof pagination === "function"
          ? pagination(paginationState)
          : pagination
      )
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  })

  return (
    <div className='space-y-4'>
      <DataTableToolbar table={table} />
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  )
}
